import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// Detect backend URL automatically based on current domain
const getBackendURL = () => {
  // If REACT_APP_BACKEND_URL is set, use it (for local development)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Otherwise, use the current domain (works for both fadaapp.com and preview URL)
  return window.location.origin;
};

const api = axios.create({
  baseURL: getBackendURL()
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Check user status every 10 seconds (if logged in)
  useEffect(() => {
    if (!token || !user) return;

    const checkUserStatus = async () => {
      try {
        const response = await api.get('/api/auth/me');
        const userData = response.data;
        
        // Check if user is blocked or deleted
        if (userData.status !== 'active') {
          alert('Sua conta foi bloqueada. Você será desconectado.');
          logout();
        } else {
          // Update user data if still active
          setUser(userData);
        }
      } catch (error) {
        // If 401 or 404, user was deleted or token invalid
        if (error.response?.status === 401 || error.response?.status === 404) {
          alert('Sua conta foi removida ou sua sessão expirou. Você será desconectado.');
          logout();
        }
      }
    };

    // Check immediately on mount
    checkUserStatus();

    // Then check every 10 seconds
    const interval = setInterval(checkUserStatus, 10000);

    return () => clearInterval(interval);
  }, [token, user?.id]); // Re-run if token or user ID changes

  const fetchUser = async () => {
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, deviceToken = null) => {
    const payload = { username, password };
    if (deviceToken) {
      payload.device_token = deviceToken;
    }
    
    const response = await api.post('/api/auth/login', payload);
    const data = response.data;
    
    // Check if 2FA TOTP is required (optional feature)
    if (data.requires_2fa && !data.requires_email) {
      return {
        requires_2fa: true,
        requires_email: false,
        temp_token: data.temp_token
      };
    }
    
    // Check if email verification is required (mandatory)
    if (data.requires_email) {
      return {
        requires_2fa: false,
        requires_email: true,
        needs_email_setup: data.needs_email_setup || false,
        temp_token: data.temp_token,
        email_masked: data.email_masked
      };
    }
    
    // Check if email setup is needed (user without email)
    if (data.needs_email_setup) {
      return {
        requires_2fa: false,
        requires_email: false,
        needs_email_setup: true,
        token: data.token,
        user: data.user
      };
    }
    
    // Normal login without any verification (trusted device)
    const { token: newToken, user: userData, device_token } = data;
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    
    return {
      requires_2fa: false,
      requires_email: false,
      device_token
    };
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('device_token'); // Clear device token on logout
    delete api.defaults.headers.common['Authorization'];
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await api.get('/api/auth/me');
        setUser(response.data);
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, api, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
